import {
  FilterDefs,
  GridSortConfig,
  GridTableLayout,
  HeaderAction,
  PageHeaderLayout,
  toggleFilter,
  useGridTableLayoutState,
} from "@homebound/beam";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { baseDownloadUrl } from "src/context";
import {
  Maybe,
  Order,
  ProductOfferingOrder,
  ProductOfferingsFilter,
  ReadyPlanStatus,
} from "src/generated/graphqlTypes";
import { useProjectCommunityFilter, useReadyPlanStatusFilter } from "src/hooks";
import {
  GlobalProductOfferingsPageQuery,
  useGlobalProductOfferingsPageQuery,
} from "src/routes/libraries/product-offerings/GlobalProductOfferings.generated";
import {
  createGlobalProductOfferingsColumns,
  createGlobalProductOfferingsRows,
  createGlobalProductOfferingsRowStyles,
} from "src/routes/libraries/product-offerings/GlobalProductOfferingsTable";
import { addEntityParam, createProductOfferingConfigsUrl, createProductOfferingUrl } from "src/RouteUrls";
import { isDefined } from "src/utils";
import { parseOrder, toOrder } from "src/utils/ordering";
import { openInSelf } from "src/utils/window";

type GlobalProductOfferingsProps = { developmentId?: string; projectId?: string };

export function GlobalProductOfferings(props: GlobalProductOfferingsProps) {
  const { developmentId, projectId } = props;
  const navigate = useNavigate();
  const isScoped = isDefined(developmentId) || isDefined(projectId);
  const { projectCommunityFilter } = useProjectCommunityFilter({ skip: isScoped });
  const { rpStatusFilter: status } = useReadyPlanStatusFilter({ omitArchived: true });
  const filterDefs: FilterDefs<ProductOfferingsFilterWithArchive> = useMemo(
    () => ({
      status,
      ...(!isScoped ? { project: projectCommunityFilter } : {}),
      archived: toggleFilter({ label: "Show Archived" }),
    }),
    [isScoped, projectCommunityFilter, status],
  );
  const layoutState = useGridTableLayoutState<ProductOfferingsFilterWithArchive>({
    persistedFilter: { filterDefs, storageKey: productOfferingsFilterStorageKey(developmentId, projectId) },
    search: "server",
  });
  const [orderBy, setOrderBy] = useState<ProductOfferingOrder>(defaultGlobalProductOfferingsOrder);
  const query = useGlobalProductOfferingsPageQuery({
    variables: {
      filter: mapToFilter(layoutState.filter, layoutState.searchString, { developmentId, projectId }),
      orderBy,
      page: { offset: 0, limit: PAGE_SIZE },
    },
  });
  const sorting: GridSortConfig = useMemo(
    () => ({
      on: "server",
      onSort: (key, direction) => setOrderBy(toOrder(key, direction)),
      value: parseOrder(orderBy),
    }),
    [orderBy],
  );
  const columns = useMemo(() => createGlobalProductOfferingsColumns({ hideCommunity: isScoped }), [isScoped]);
  const rowStyles = useMemo(() => createGlobalProductOfferingsRowStyles(), []);
  const createRows = useCallback(
    (data: GlobalProductOfferingsPageQuery | undefined) =>
      createGlobalProductOfferingsRows(data?.productOfferings.entities ?? []),
    [],
  );
  const maybeFetchNextPage = useCallback(async () => {
    if (query.data?.productOfferings.pageInfo.hasNextPage) {
      await query.fetchMore({
        variables: { page: { offset: query.data.productOfferings.entities.length, limit: PAGE_SIZE } },
      });
    }
  }, [query]);
  const headerActions: HeaderAction[] = useMemo(
    () => [
      {
        label: "Create",
        onClick: () => navigate(createProductOfferingUrl(addEntityParam, undefined, developmentId, projectId)),
      },
      ...(isScoped
        ? [
            {
              kind: "menu",
              trigger: { icon: "verticalDots", variant: "outline" },
              tooltip: "Actions",
              items: [
                { label: "Compare Configurations", onClick: createProductOfferingConfigsUrl(developmentId, projectId) },
                {
                  label: "Download Plan Package Diff",
                  onClick: () => {
                    const params = new URLSearchParams({
                      type: "planPackageScopeDiff",
                      projectId: projectId ?? developmentId ?? "",
                    });
                    openInSelf(`${baseDownloadUrl()}/xlsx?${params.toString()}`);
                  },
                },
              ],
            } satisfies HeaderAction,
          ]
        : []),
    ],
    [developmentId, isScoped, navigate, projectId],
  );

  return (
    <PageHeaderLayout pageHeader={{ title: "Product Offerings", actions: headerActions }}>
      <GridTableLayout
        defaultView="card"
        layoutState={layoutState}
        withCardView
        tableProps={{
          query,
          createRows,
          columns,
          sorting,
          rowStyles,
          as: "virtual",
          emptyFallback: "No Product Offerings Found",
          infiniteScroll: { onEndReached: maybeFetchNextPage },
        }}
      />
    </PageHeaderLayout>
  );
}

type ProductOfferingsFilterWithArchive = ProductOfferingsFilter & { archived?: Maybe<boolean> };

export const defaultGlobalProductOfferingsFilter: ProductOfferingsFilter = {
  effectiveStatus: [ReadyPlanStatus.Active, ReadyPlanStatus.Draft],
};

function mapToFilter(
  filter: ProductOfferingsFilterWithArchive,
  searchString: string | undefined,
  scope: { developmentId?: string; projectId?: string },
): ProductOfferingsFilter {
  const { archived, status, ...rest } = filter;
  const { developmentId, projectId } = scope;

  return {
    ...rest,
    ...(status?.nonEmpty ? { effectiveStatus: status } : archived ? {} : defaultGlobalProductOfferingsFilter),
    ...(searchString && { search: searchString }),
    ...(isDefined(developmentId) ? { development: [developmentId] } : {}),
    ...(isDefined(projectId) ? { project: [projectId] } : {}),
  };
}

function productOfferingsFilterStorageKey(developmentId?: string, projectId?: string): string {
  if (isDefined(developmentId)) return "development-product-offering-filter";
  if (isDefined(projectId)) return "project-product-offering-filter";
  return "global-product-offering-filter";
}

const PAGE_SIZE = 40;
export const defaultGlobalProductOfferingsPage = { offset: 0, limit: PAGE_SIZE };
export const defaultGlobalProductOfferingsOrder: ProductOfferingOrder = { name: Order.Asc };
